import { Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Search, ShoppingCart, User, Menu, X, LogOut, LayoutDashboard, Package } from "lucide-react";
import { useAuth } from "../lib/auth";
import { useCart } from "../lib/cart";
import { useSettings } from "../lib/settings";
import { fileUrl } from "../lib/api";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "./ui/dropdown-menu";

const nav = [
  { to: "/", label: "الرئيسية" },
  { to: "/supplements", label: "المكملات" },
  { to: "/sportswear", label: "الملابس" },
  { to: "/accessories", label: "الإكسسوارات" },
];

export default function Header({ transparentOverHero = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [q, setQ] = useState("");
  const { user, logout } = useAuth();
  const { count } = useCart();
  const { settings } = useSettings();
  const storeName = settings?.store_name || "POWER GYM";
  const storeLogo = settings?.logo_url ? fileUrl(settings.logo_url) : "";
  const nav_ = useNavigate();

  useEffect(() => {
    if (!transparentOverHero) { setScrolled(true); return; }
    const on = () => setScrolled(window.scrollY > 40);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, [transparentOverHero]);

  const onSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav_(`/supplements?q=${encodeURIComponent(q.trim())}`);
  };

  return (
    <header
      data-testid="site-header"
      style={{ top: "var(--pg-promo-h, 0px)" }}
      className={`fixed inset-x-0 z-50 transition-colors duration-300 border-b ${scrolled ? "bg-[#191A1D]/95 backdrop-blur-md border-[#3A3D43]" : "bg-transparent border-transparent"}`}
    >
      <div className="pg-container flex items-center justify-between h-16 md:h-20 gap-4">
        {/* Logo */}
        <Link to="/" data-testid="logo-link" className="flex items-center gap-2 shrink-0">
          {storeLogo ? (
            <img src={storeLogo} alt={storeName} className="w-10 h-10 rounded-md object-contain bg-[#24262B] border border-[#3A3D43] p-0.5" />
          ) : (
            <div className="w-9 h-9 rounded-md bg-gradient-to-br from-[#EF1B17] to-[#FF3B30] flex items-center justify-center font-black text-white pg-red-glow-soft">
              {storeName.slice(0, 2).toUpperCase()}
            </div>
          )}
          <span className="hidden sm:block font-black tracking-tight text-[#F4F4F2]">{storeName}</span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-1">
          {nav.map((n) => (
            <Link key={n.to} to={n.to} data-testid={`nav-${n.to.replace("/", "") || "home"}`}
              className="px-4 py-2 text-sm font-semibold text-[#F4F4F2]/85 hover:text-[#EF1B17] transition-colors">
              {n.label}
            </Link>
          ))}
        </nav>

        {/* Search */}
        <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-sm relative">
          <input
            data-testid="header-search-input"
            value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="ابحث عن منتج أو وكالة..."
            className="w-full bg-[#24262B] border border-[#3A3D43] rounded-md pl-4 pr-10 py-2 text-sm text-[#F4F4F2] placeholder:text-gray-500 focus:border-[#EF1B17] focus:outline-none"
          />
          <button data-testid="header-search-btn" type="submit" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-[#EF1B17]">
            <Search size={18} />
          </button>
        </form>

        {/* Right actions */}
        <div className="flex items-center gap-2">
          <Link to="/cart" data-testid="cart-icon-btn" className="relative p-2 hover:text-[#EF1B17] transition-colors">
            <ShoppingCart size={22} />
            {count > 0 && (
              <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 bg-[#EF1B17] text-white text-[10px] font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {count}
              </span>
            )}
          </Link>

          {user === undefined ? null : user ? (
            <DropdownMenu>
              <DropdownMenuTrigger data-testid="user-menu-trigger" className="p-2 hover:text-[#EF1B17]">
                <User size={22} />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="bg-[#24262B] border-[#3A3D43] text-[#F4F4F2] min-w-[200px]">
                <div className="px-3 py-2 text-xs text-gray-400">مرحباً {user.full_name}</div>
                <DropdownMenuSeparator className="bg-[#3A3D43]" />
                {user.role === "admin" && (
                  <DropdownMenuItem data-testid="menu-admin" onClick={() => nav_("/admin")} className="cursor-pointer">
                    <LayoutDashboard size={16} className="ms-2" /> لوحة التحكم
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem data-testid="menu-orders" onClick={() => nav_("/account")} className="cursor-pointer">
                  <Package size={16} className="ms-2" /> طلباتي
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-[#3A3D43]" />
                <DropdownMenuItem data-testid="menu-logout" onClick={async () => { await logout(); nav_("/"); }} className="cursor-pointer text-[#EF1B17]">
                  <LogOut size={16} className="ms-2" /> تسجيل الخروج
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Link to="/login" data-testid="login-btn" className="hidden sm:inline-flex items-center gap-2 bg-[#EF1B17] hover:bg-[#FF3B30] text-white text-sm font-bold px-4 py-2 rounded-md pg-lift">
              <User size={16} /> دخول
            </Link>
          )}

          {/* Mobile menu */}
          <Sheet>
            <SheetTrigger data-testid="mobile-menu-btn" className="lg:hidden p-2">
              <Menu size={22} />
            </SheetTrigger>
            <SheetContent side="right" className="bg-[#191A1D] border-[#3A3D43] text-[#F4F4F2] w-72">
              <div className="flex flex-col gap-1 mt-8">
                {nav.map((n) => (
                  <Link key={n.to} to={n.to} data-testid={`mobile-nav-${n.to.replace("/", "") || "home"}`}
                    className="px-4 py-3 rounded-md hover:bg-[#24262B] text-lg font-semibold">
                    {n.label}
                  </Link>
                ))}
                {!user && (
                  <Link to="/login" className="mt-4 mx-2 bg-[#EF1B17] hover:bg-[#FF3B30] text-white font-bold py-3 rounded-md text-center">
                    تسجيل الدخول
                  </Link>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
